<script setup lang="ts">
  import type { IHeaderSection, IHeaderTabs } from '@/components/main-header/types'
  import { useMainHeaderStore } from '@/components/main-header/store'
  import { useGlobalSettingsStore } from '@/stores/GlobalSettings.store'
  import useHeaderDataLayer from '@/composables/data-layers/useHeaderDataLayer'

  const tabs = ref<IHeaderTabs[]>([])
  const sections = ref<IHeaderSection[]>([])
  const mainHeaderStore = useMainHeaderStore()
  const globalSettingsStore = useGlobalSettingsStore()
  const { locale } = useI18n()

  const { isMenuOpen, activeSection } = storeToRefs(mainHeaderStore)
  const { headerFlightStatusLink } = storeToRefs(globalSettingsStore)

  const { data, error } = await useFetch('/api/storyblok/main-navigation', {
    query: { language: locale.value }
  })

  const { pushHeaderBurgerMenuClickDataLayer } = useHeaderDataLayer()

  if (!error.value) {
    tabs.value = data.value?.tabs || []
    sections.value = data.value?.sections || []
  } else {
    if (isDev()) {
      console.error('Error fetching main navigation data:', error.value)
    }
  }

  function onBurgerClick() {
    pushHeaderBurgerMenuClickDataLayer()
    mainHeaderStore.isMenuOpen = true
  }
</script>

<template>
  <DialogRoot v-model:open="isMenuOpen">
    <DialogTrigger as-child>
      <button
        class="flex size-10 items-center justify-center rounded-full bg-bt-green"
        :aria-label="$t('aria.label.hamburgerMenu')"
        :aria-expanded="isMenuOpen"
        @click="onBurgerClick"
      >
        <Icon name="common/menu-burger" />
      </button>
    </DialogTrigger>

    <DialogPortal>
      <DialogOverlay
        :aria-label="$t('aria.label.close')"
        class="fixed inset-0 z-40 bg-black/40 data-[state=closed]:animate-menu-overlay-out data-[state=open]:animate-menu-overlay-in"
        @click="isMenuOpen = false"
      />

      <DialogContent
        class="shadow-xl fixed inset-x-10 top-0 z-50 max-h-screen overflow-y-auto rounded-lg bg-white focus:outline-none data-[state=closed]:animate-menu-drop-out data-[state=open]:animate-menu-drop-in"
      >
        <div class="relative h-20 border-b border-divider">
          <MainHeaderNavigation :header-data="{ tabs, sections }" />

          <DialogClose as-child>
            <button
              class="absolute right-6 top-6 flex size-10 items-center justify-center p-1"
              :aria-label="$t('aria.label.close')"
            >
              <Icon name="common/close" />
            </button>
          </DialogClose>
        </div>

        <MainHeaderSectionsDropdown
          v-show="isMenuOpen"
          :active-section="activeSection"
          @close="activeSection = null"
        />

        <div
          v-if="headerFlightStatusLink"
          v-show="isMenuOpen"
          class="mx-auto max-w-354 border-t border-divider px-24 py-8"
        >
          <MainHeaderFlightStatusLink :link="headerFlightStatusLink" />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
